import { AgTechClickEventHandler, AgTechButtonProps, AgTechButton } from "@agtech/web/components/Buttons/AgTechButtons";
import AgTechViewDialog from "@agtech/web/components/Portals/Dialogs/AgTechViewDialog";
import { InvoicePaymentDataGetInvoicePaymentsQuery } from "@sales/data/invoicing/payments/InvoicePaymentDataQueries";
import InvoicePaymentsGrid from "../../components/grids/InvoicePaymentsGrid";
import { useAgTechQuery, useAgTechQueryCombination, useAgTechQueryResultCombination } from "@agtech/core/data/queries/AgTechDataQueries";
import { ContactDataGetAllContactsQuery } from "@agtech/data/contacts/ContactDataQueries";
import { useInvoiceDataGetInvoiceDetailsQuery } from "@sales/data/invoicing/InvoicingDataQueries";
import { AgTechFormRow } from "@agtech/web/components/Forms/Layout/AgTechFormRow";
import { AgTechFormReadOnlyTextControl } from "@agtech/web/components/Forms/Controls/AgTechFormReadOnlyTextControl";
import dayjs from "dayjs";
import { InvoiceStatus } from "@sales/data/invoicing/InvoicingData";
import { AgTechFormSection } from "@agtech/web/components/Forms/Layout/AgTechFormSection";
import AddPaymentDialog from "./InvoiceAddPaymentDialog";

export type ViewPaymentsDialogProps = {
    invoiceId: number
}

const ViewPaymentsDialog = (props: ViewPaymentsDialogProps) => {
    const dialogQuery = usePaymentsDialogQuery({
        invoiceId: props.invoiceId
    });

    return (
        <AgTechViewDialog
            data={{
                query: dialogQuery
            }}
            structure={{
                header: dialogData => ({
                    //header: `Invoice Payments (${dialogData.payments.length})`,
                    header: `Invoice Payments`,
                    detail: `View and edit payments for invoice #${dialogData.invoiceDetails.invoice.info.number}`
                }),
                width: "650px"
            }}
        >
            {(data) => {
                const totalInvoicePaymentsMade = data.invoiceDetails.invoice.details.paymentsMade
                    .reduce((acc, p) => acc + p.amount, 0);

                const totalAmountDue = data.invoiceDetails.invoice.info.total - totalInvoicePaymentsMade;

                return (
                    <div className="stretched p-body row-based flex">
                        <AgTechFormRow classes="pt-1">
                            <AgTechFormReadOnlyTextControl
                                entityType={data}
                                caption='Invoice Number'
                                value={() => '#' + data.invoiceDetails.invoice.info.number}
                                classes='flexed'
                            />
                            <AgTechFormReadOnlyTextControl
                                entityType={data}
                                caption='Invoice Date'
                                value={() => dayjs(data.invoiceDetails.invoice.info.date).format('MM/DD/YYYY')}
                                classes='flexed'
                            />
                            <AgTechFormReadOnlyTextControl
                                entityType={data}
                                caption='Status'
                                value={() => InvoiceStatus[data.invoiceDetails.invoice.info.status]}
                                classes='flexed'
                            />
                        </AgTechFormRow>
                        <AgTechFormRow classes="pb-8">
                            <AgTechFormReadOnlyTextControl
                                entityType={data}
                                caption='Invoiced Amount'
                                value={() => '$' + data.invoiceDetails.invoice.info.total.toFixed(2)}
                                classes='flexed'
                            />
                            <AgTechFormReadOnlyTextControl
                                entityType={data}
                                caption='Payments Made'
                                value={() => '$' + totalInvoicePaymentsMade.toFixed(2)}
                                classes='flexed'
                            />
                            <AgTechFormReadOnlyTextControl
                                entityType={data}
                                caption='Amount Due'
                                value={() => '$' + totalAmountDue.toFixed(2)}
                                classes='flexed'
                            />
                        </AgTechFormRow>
                        <AgTechFormSection
                            header="Payments Made"
                            headerContent={() => (
                                <AddPaymentDialog.Button
                                    invoiceId={props.invoiceId}
                                    classes="button-link pr-0"
                                    icon="fas fa-plus"
                                />
                            )}
                            classes="flexed"
                        >
                            <InvoicePaymentsGrid
                                invoiceId={props.invoiceId}
                                contacts={data.contacts}
                                payments={data.payments}
                                addActions={true}
                            />
                        </AgTechFormSection>
                    </div>
                )
            }}
        </AgTechViewDialog>
    )
}

const usePaymentsDialogQuery = (props: ViewPaymentsDialogProps) => {
    const invoiceDetailsQuery = useInvoiceDataGetInvoiceDetailsQuery({
        invoiceId: props.invoiceId
    });

    const invoicePaymentsQuery = useAgTechQuery({
        query: InvoicePaymentDataGetInvoicePaymentsQuery,
        queryContext: props.invoiceId
    });

    const contactsQuery = useAgTechQuery({
        query: ContactDataGetAllContactsQuery
    });

    const invoiceDataQuery = useAgTechQueryResultCombination({
        firstQuery: invoiceDetailsQuery,
        secondQuery: invoicePaymentsQuery,
        projection: (invoiceDetails, payments) => ({
            invoiceDetails,
            payments
        })
    });

    return useAgTechQueryResultCombination({
        firstQuery: invoiceDataQuery,
        secondQuery: contactsQuery,
        projection: (invoiceData, contacts) => ({
            ...invoiceData,
            contacts
        })
    });
}

ViewPaymentsDialog.Open = (e: AgTechClickEventHandler, props: ViewPaymentsDialogProps) => e.dialogs.openDialog({
    dialog: () => <ViewPaymentsDialog {...props} />
});

ViewPaymentsDialog.Button = (props: AgTechButtonProps & ViewPaymentsDialogProps) => {
    let buttonProps: AgTechButtonProps = {
        text: 'View Payments',
        icon: 'bi bi-credit-card-2-front-fill',
        ...props,
        action: e => ViewPaymentsDialog.Open(e, props)
    }

    return <AgTechButton {...buttonProps} />
}

export default ViewPaymentsDialog;